import { useState, useRef, useEffect } from 'react';
import { Bell, MessageCircle, Calendar, Clock, Check, CheckCheck } from 'lucide-react';
import { useNotifications, useUnreadCount, useMarkRead, useMarkAllRead } from '@/hooks/useNotifications';
import type { Notification, NotificationType } from '@/types';
import { formatDistanceToNow } from 'date-fns';

interface NotificationCenterProps {
  onMentionClick?: (messageId: string) => void;
  onShiftClick?: (date: string) => void;
}

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'mention':
      return <MessageCircle size={16} className="text-blue-400" />;
    case 'shift_assigned':
      return <Calendar size={16} className="text-emerald-400" />;
    case 'shift_unassigned':
      return <Calendar size={16} className="text-red-400" />;
    case 'shift_reminder':
      return <Clock size={16} className="text-yellow-400" />;
    default:
      return <Bell size={16} className="text-gray-400" />;
  }
}

export function NotificationCenter({ onMentionClick, onShiftClick }: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { data: notifications = [] } = useNotifications();
  const { data: unreadCount = 0 } = useUnreadCount();
  const markReadMutation = useMarkRead();
  const markAllReadMutation = useMarkAllRead();

  // Close panel when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }

    if (notification.type === 'mention' && notification.reference_id && onMentionClick) {
      onMentionClick(notification.reference_id);
    } else if (
      (notification.type === 'shift_assigned' || notification.type === 'shift_unassigned' || notification.type === 'shift_reminder') &&
      notification.reference_id && onShiftClick
    ) {
      onShiftClick(notification.reference_id);
    }

    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Bell button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-slate-700 rounded-lg transition-colors"
        title="Notifications"
        data-testid="clickable-notifications-bell"
      >
        <Bell size={22} className="text-gray-400 hover:text-white" />
        {unreadCount > 0 && (
          <span
            data-testid="container-notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="fixed sm:absolute right-2 sm:right-0 top-14 sm:top-full sm:mt-2 w-[calc(100vw-1rem)] sm:w-96 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 max-h-[70vh] flex flex-col"
          data-testid="container-notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-slate-700">
            <h3 className="text-white font-semibold text-sm">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllReadMutation.mutate()}
                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                data-testid="clickable-mark-all-read"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500 text-sm">
                No notifications yet
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`clickable-notification-${notification.id}`}
                  className={`w-full text-left p-3 border-b border-slate-700/50 hover:bg-slate-700/50 transition-colors flex gap-3 ${
                    notification.is_read ? 'opacity-60' : ''
                  }`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-white text-sm font-medium truncate">
                        {notification.title}
                      </p>
                      {!notification.is_read && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">
                      {notification.body}
                    </p>
                    <p className="text-gray-500 text-[10px] mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
